import {
  Aperture,
  Building2,
  Cpu,
  KeyRound,
  LayoutGrid,
  LucideIcon,
  Map,
  MapPin,
  Package,
  ShieldAlert,
  ShieldCheck,
  Tag,
  Users,
} from 'lucide-react';

/**
 * The single source of truth for routing structure. Route gates (App.tsx),
 * the sidebar (Layout.tsx), page h1s (PageHeader), and document.title all
 * derive from this table — permission lists and titles cannot drift apart.
 */
export interface RouteMeta {
  /** Absolute path. */
  path: string;
  /** h1, sidebar label, and document.title. */
  title: string;
  /** Teaching line rendered under the page h1. */
  description?: string;
  icon?: LucideIcon;
  /** OR-logic permission list; omit for any authenticated member. */
  permissions?: string[];
  /** Sidebar section; omit to keep the route out of the nav. */
  nav?: 'main' | 'admin';
  /** Renders as an indented child item in the sidebar. */
  indent?: boolean;
  /**
   * Renders as a collapsible group header whose children are the `indent`
   * routes sitting directly beneath it in this list. Expanded by default.
   */
  collapsible?: boolean;
  /** Additional pathname prefixes that keep this nav item active. */
  activePrefixes?: string[];
  /**
   * Absolute path of the page this one belongs to. ONLY for pages kept out of
   * the nav (`nav` undefined): while it is open the parent's sidebar row is the
   * current one, and PageHeader offers '← {parent title}' when the caller may
   * open the parent (useParentLink). A visible indented child (Roles) never
   * sets it — the sidebar already shows where it sits.
   */
  parent?: string;
  /**
   * 'fill': at lg the page is a fixed-height column and its list's table region
   * is the only scroller (DESIGN_SYSTEM.md §5 Lists). Every full-page list sets it.
   */
  layout?: 'fill';
}

/** One sub-page of /system-admin. */
export interface SystemAdminTabMeta {
  /** URL segment under /system-admin. */
  value: string;
  label: string;
  /** The single permission that reveals this page in the nav and mounts it. */
  permission: string;
  /** Teaching line under the page h1. */
  description: string;
  icon: LucideIcon;
  /**
   * Keeps the page out of the sidebar while leaving its route intact.
   *
   * For a surface that belongs to another one rather than beside it: License
   * Keys exists to let an organization be created, so it is reached from
   * Organizations. Its URL still works and is still linkable — a hidden page is
   * not a deleted one.
   */
  hiddenFromNav?: boolean;
  /** The sibling tab (`value`) a hidden tab belongs to; see RouteMeta.parent. */
  parent?: string;
}

/**
 * The system-admin tab registry, and the only place a tab is declared.
 *
 * It lives here rather than in SystemAdmin.tsx because App.tsx needs it to
 * register the routes and SystemAdmin.tsx is lazily imported — reading it from
 * there would pull the whole system-admin chunk into the initial bundle.
 *
 * Four things derive from this list: the router paths in App.tsx, the OR-logic
 * gate on /system-admin, the tab strip, and the bare-path redirect. They used
 * to be four separate literals, and adding the License Keys tab to three of
 * them produced a working sidebar entry over a "Page not found" — the E2E
 * suite is what caught it. One list means that cannot happen again.
 *
 * `as const` rather than a plain annotation so `value` is a literal union
 * rather than `string`. That is what makes SystemAdmin.tsx's panel switch
 * exhaustive: a tab added here with no panel is a type error, not an empty tab
 * nobody notices until they click it.
 */
// Labels follow DESIGN_SYSTEM.md §6 "Naming": a label adds "All" only where it
// would otherwise exactly match a main-nav label (All Templates, All Sensors,
// All Lenses). An administrator holding both permissions would otherwise see
// two links with the identical label — indistinguishable to a screen reader.
export const SYSTEM_ADMIN_TABS = [
  {
    value: 'users', label: 'Users', permission: 'system_users:read', icon: Users,
    description: 'Every user account across the platform.',
  },
  {
    value: 'organizations', label: 'Organizations', permission: 'system_orgs:read', icon: Building2,
    description: 'Every organization, its members, and what it owns.',
  },
  {
    value: 'processor-templates', label: 'All Templates', permission: 'system_processor_templates:read', icon: Package,
    description: 'Review template versions before they run against sensors.',
  },
  {
    value: 'processors', label: 'Running Processors', permission: 'system_processors:read', icon: Cpu,
    description: 'Processors running right now, across every organization.',
  },
  {
    value: 'sensors', label: 'All Sensors', permission: 'system_sensors:read', icon: MapPin,
    description: 'Every registered device, and who can see it.',
  },
  {
    value: 'lenses', label: 'All Lenses', permission: 'system_lenses:read', icon: Aperture,
    description: 'Every lens on the platform, including other organizations’.',
  },
  {
    value: 'license-keys', label: 'License Keys', permission: 'system_license_keys:read', icon: KeyRound,
    description: 'Keys that authorize creating a new organization.',
    // Reached from Organizations, not from the sidebar: a licence key exists to
    // let an organization be created, so it belongs next to the organizations
    // rather than as an eighth sibling of them.
    hiddenFromNav: true,
    parent: 'organizations',
  },
] as const satisfies readonly SystemAdminTabMeta[];

export type SystemAdminTabValue = (typeof SYSTEM_ADMIN_TABS)[number]['value'];

/** OR-logic gate on /system-admin: any one tab's permission opens the route. */
export const SYSTEM_ADMIN_PERMISSIONS: string[] = SYSTEM_ADMIN_TABS.map((tab) => tab.permission);

/** Router paths for /system-admin and each of its tabs, relative to the shell. */
export const SYSTEM_ADMIN_ROUTE_PATHS = [
  'system-admin',
  ...SYSTEM_ADMIN_TABS.map((tab) => `system-admin/${tab.value}`),
];

export const ROUTES: RouteMeta[] = [
  {
    path: '/my-apps',
    layout: 'fill',
    title: 'App Clients',
    description: 'App clients that authenticate against the Dory API.',
    icon: LayoutGrid,
    permissions: ['app_clients:read'],
    nav: 'main',
  },
  {
    path: '/my-processor-templates',
    layout: 'fill',
    title: 'Processor Templates',
    description: 'Publish and version the container images your processors run.',
    icon: Package,
    permissions: ['processor_templates:read'],
    nav: 'main',
  },
  {
    path: '/lenses',
    layout: 'fill',
    title: 'Lenses',
    description: 'Named groups of processor templates that subscribers connect to by alias.',
    icon: Aperture,
    permissions: ['lenses:read'],
    nav: 'main',
    activePrefixes: ['/lenses/'],
  },
  {
    path: '/sensors',
    layout: 'fill',
    title: 'Sensors',
    description: 'Field devices reporting events into your organization’s streams.',
    icon: MapPin,
    permissions: ['sensors:read'],
    nav: 'main',
  },
  {
    path: '/tags',
    layout: 'fill',
    title: 'Event Types',
    description: 'The kinds of event your processors emit and your subscriptions request.',
    icon: Tag,
    permissions: ['tags:read'],
    nav: 'main',
  },
  {
    path: '/my-org',
    layout: 'fill',
    title: 'Organization',
    description: 'Members, invitations, and role assignments.',
    icon: Building2,
    permissions: ['org_members:read'],
    nav: 'main',
  },
  {
    path: '/my-org/roles',
    layout: 'fill',
    title: 'Roles',
    description: 'Roles in your organization and the permissions they grant. Built-in roles are read-only.',
    icon: ShieldCheck,
    permissions: ['org_roles:read'],
    nav: 'main',
    indent: true,
  },
  {
    path: '/app-request-builder',
    title: 'Request Builder',
    description: 'Pick geo areas and event types, then generate a Python subscription snippet.',
    icon: Map,
    permissions: ['subscriptions:write'],
    nav: 'main',
    activePrefixes: ['/geo-area-builder'],
  },
  {
    path: '/account-settings',
    title: 'Account Settings',
    description: 'Your profile, email address, and password.',
  },
  {
    path: '/system-admin',
    title: 'System Admin',
    description: 'God-mode controls. Actions here affect the entire system.',
    icon: ShieldAlert,
    permissions: SYSTEM_ADMIN_PERMISSIONS,
    nav: 'admin',
    collapsible: true,
    activePrefixes: ['/system-admin/'],
  },
  /*
   * One nav item and one routed page per system-admin surface, derived from the
   * registry above rather than hand-written — the six used to be tabs inside a
   * single page, which meant one entry in the sidebar and no way to link a
   * colleague to "the license keys screen".
   *
   * Each carries its OWN permission rather than the OR-union: previously any
   * one system read permission opened /system-admin/* and the page then had to
   * detect a tab the caller could not see and render AccessDenied inside its
   * own shell. Per-route permissions mean the standard PermissionGate answers
   * that, exactly as it does everywhere else.
   */
  ...SYSTEM_ADMIN_TABS.map((tab): RouteMeta => ({
    path: `/system-admin/${tab.value}`,
    title: tab.label,
    description: tab.description,
    icon: tab.icon,
    permissions: [tab.permission],
    // A hidden tab keeps its route, its gate and its title, and loses only its
    // sidebar entry — which is what `nav: undefined` means to the shell.
    nav: 'hiddenFromNav' in tab && tab.hiddenFromNav ? undefined : 'admin',
    parent: 'parent' in tab && tab.parent ? `/system-admin/${tab.parent}` : undefined,
    indent: true,
    layout: 'fill',
  })),
];

/** Resolve the registry entry for a pathname (longest matching route wins). */
export function findRouteMeta(pathname: string): RouteMeta | undefined {
  return (
    ROUTES.find((route) => route.path === pathname) ??
    ROUTES.filter((route) =>
      [route.path + '/', ...(route.activePrefixes ?? [])].some((prefix) =>
        pathname.startsWith(prefix),
      ),
    ).sort((a, b) => b.path.length - a.path.length)[0]
  );
}

/**
 * A page's h1 and description, read from this registry by exact path. Pages
 * call it at module scope (`const PAGE = routeCopy('/my-apps')`) instead of
 * retyping the strings, so a rename here reaches the sidebar, the h1 and
 * document.title together. Throws for an unregistered path, so a typo fails
 * the first test that renders the page.
 */
export function routeCopy(path: string): { title: string; description?: string } {
  const route = ROUTES.find((candidate) => candidate.path === path);
  if (!route) throw new Error(`routeCopy: no route registered at ${path}`);
  return { title: route.title, description: route.description };
}

/** True when the nav item for `route` should render as active. */
export function isRouteActive(route: RouteMeta, pathname: string): boolean {
  // A collapsible group header is a heading, not a destination — the sidebar
  // renders it as a toggle button that navigates nowhere, and every path
  // beneath it belongs to one of its children. Checked BEFORE the exact-path
  // match below, which would otherwise light the header on its own bare path
  // and give the sidebar two current rows at once.
  if (route.collapsible) return false;
  if (pathname === route.path) return true;
  // A page kept out of the nav lights its parent's row, so exactly one row is
  // current while it is open (DESIGN_SYSTEM.md §9).
  if (ROUTES.some((other) => !other.nav && other.parent === route.path && other.path === pathname)) return true;
  // "/" is the index redirect target for App Clients.
  if (route.path === '/my-apps' && pathname === '/') return true;
  const prefixes = [...(route.activePrefixes ?? [])];
  // A parent route is not active while an indented child route matches exactly.
  const childMatch = ROUTES.some(
    (other) => other !== route && other.indent && other.path === pathname && other.path.startsWith(route.path),
  );
  if (childMatch) return false;
  if (route.path === '/my-org' && pathname.startsWith('/my-org/') && pathname !== '/my-org/roles') {
    return true;
  }
  return prefixes.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
}
